import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Button,
  DatePicker,
  Empty,
  Form,
  message,
  Modal,
  Select,
  Space,
  Spin,
  Tabs,
  Tag,
  Tooltip,
  Tour,
} from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { PDFDownloadLink } from "@react-pdf/renderer";
import dayjs from "dayjs";
import PvTable from "../../components/Table/PvTable";
import Popup from "../../components/Popup";
import usePrismPaymentService from "../../services/Prism/paymentService";
import usePrismSubscriptionService from "../../services/Prism/subscriptionService";
import { setPrismReportsTab } from "../../state/slices/generalSlice";
import MonthlySpendChart, {
  buildMonthlyExpenditureData,
  formatSubscriptionBreakdown,
  getPaymentSubscriptionName,
  getTotalMonthlySpend,
  MONTHLY_EXPENDITURE_COLUMN_LABELS,
} from "./MonthlySpendChart";
import PrismPaymentForm from "./PrismPaymentForm";
import PrismSubscriptionDetailsModal from "./PrismSubscriptionDetailsModal";
import PrismTruncatedText from "./PrismTruncatedText";
import {
  buildSubscriptionReportRows,
  isDateInRange,
} from "./prismReportDataUtils";
import {
  DEFAULT_REMARKS_ACCENT,
  normalizeRemarksAccent,
} from "./prismPaymentUtils";
import PrismReportPdfDocument, {
  downloadCsv,
} from "./prismReportExportUtils";
import "../../styles/pages/prism/prismReports.scss";
import {
  CRITICALITY_COLORS,
  CRITICALITY_LABELS,
  canManageAnyPrismOffice,
  canManagePrismRecord,
  canManagePrismScope,
  formatDate,
  formatLabel,
  formatOfficeName,
  formatSubOfficeName,
  getDateTime,
  isFullPrismRecord,
  LIMITED_PRISM_SUBSCRIPTION_FIELD_KEYS,
  renderRestrictedValue,
  STATUS_COLORS,
  STATUS_FILTER_OPTIONS,
} from "./prismSubscriptionUtils";

const { RangePicker } = DatePicker;

const CRITICALITY_FILTER_OPTIONS = Object.entries(CRITICALITY_LABELS).map(
  ([value, label]) => ({
    value,
    label,
  }),
);

const REGULAR_USER_SUBSCRIPTION_COLUMN_WIDTHS = {
  subscription_name: "24%",
  subscription_details: "32%",
  subscriber_department: "22%",
  subscriber_sub_department: "22%",
};

const renderCriticality = (criticality) => {
  if (!criticality) return "-";

  return (
    <Tag
      color={CRITICALITY_COLORS[criticality] || "default"}
      style={
        criticality === "overdue"
          ? { color: "#fff", borderColor: "#000" }
          : undefined
      }
    >
      {CRITICALITY_LABELS[criticality] || formatLabel(criticality)}
    </Tag>
  );
};

const renderStatus = (status) => {
  if (!status) return "-";

  return (
    <Tag color={STATUS_COLORS[status] || "blue"}>{formatLabel(status)}</Tag>
  );
};

const formatSubscriberEmails = (subscription = {}) =>
  [
    subscription.subscriber_email,
    ...(subscription.subscriber_additional_emails || []),
  ]
    .filter(Boolean)
    .join(", ");

const renderAutoRenewal = (value) => (
  <Tag color={value ? "green" : "default"}>{value ? "Enabled" : "Disabled"}</Tag>
);

const renderBudget = (value) => {
  if (value === null || value === undefined || value === "") return "-";

  const amount = Number(value);

  if (Number.isNaN(amount)) return value;

  return `PHP ${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const renderText = (value) => value || "-";

const getPaymentRemarksDisplay = (value) =>
  typeof value === "string" && value.trim() ? value : "-";

const comparePaymentsByPaidDate = (a, b) => {
  const paidDateCompare =
    getDateTime(a.date_paid) - getDateTime(b.date_paid);

  if (paidDateCompare !== 0) {
    return paidDateCompare;
  }

  return Number(a.id || 0) - Number(b.id || 0);
};

const renderSubscriptionOption = (option) => (
  <PrismTruncatedText value={option?.label} />
);

const renderSubscriptionLabel = ({ label, value }) => (
  <PrismTruncatedText value={label ?? value} />
);

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (typeof error === "string") return error;
  if (typeof error?.message === "string") return error.message;

  const firstFieldErrors = Object.values(error?.errors || {})[0];
  if (Array.isArray(firstFieldErrors) && firstFieldErrors.length > 0) {
    return firstFieldErrors[0];
  }

  return fallback;
};

const getReportFileDate = () => dayjs().format("YYYY-MM-DD");

const getDateRangeLabel = (dateRange) => {
  if (!dateRange || !dateRange[0] || !dateRange[1]) return "All";
  return `${dateRange[0].format("YYYY-MM-DD")} to ${dateRange[1].format(
    "YYYY-MM-DD",
  )}`;
};

const getPaymentSubscriptionFilterValue = (payment) =>
  payment.subscription_id
    ? String(payment.subscription_id)
    : `deleted:${payment.subscription_name_snapshot || payment.id}`;

const getPaymentScope = (payment) =>
  payment.subscription
    ? {
        office: payment.subscription.subscriber_department || null,
        unit: payment.subscription.subscriber_sub_department || null,
      }
    : {
        office: payment.subscriber_department_snapshot || null,
        unit: payment.subscriber_sub_department_snapshot || null,
      };

const PrismReports = () => {
  const [editPaymentForm] = Form.useForm();
  const [renewalDateRange, setRenewalDateRange] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [criticalityFilter, setCriticalityFilter] = useState(null);
  const [paidDateRange, setPaidDateRange] = useState(null);
  const [subscriptionFilter, setSubscriptionFilter] = useState(null);
  const [monthlyYear, setMonthlyYear] = useState(dayjs());
  const [monthlySubscriptionFilter, setMonthlySubscriptionFilter] =
    useState(null);
  const [isEditPaymentModalOpen, setIsEditPaymentModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [editPaymentSubmitError, setEditPaymentSubmitError] = useState(null);
  const [deletePaymentSubmitError, setDeletePaymentSubmitError] =
    useState(null);
  const [isTourOpen, setIsTourOpen] = useState(false);
  const reportTabsRef = useRef(null);
  const subscriptionFiltersRef = useRef(null);
  const subscriptionTableRef = useRef(null);
  const dispatch = useDispatch();
  const activeReportTab = useSelector(
    (state) => state.generalSlice.prismReportsCurrentTab,
  );
  const { details = {} } = useSelector((state) => state.accountDetailSlice);
  const {
    getSubscriptions,
    subscriptionLoading,
    subscriptionResponse,
    subscriptionError,
    getSubscriptionExportData,
    subscriptionExportLoading,
    subscriptionExportResponse,
    subscriptionExportError,
  } = usePrismSubscriptionService();
  const {
    getPayments,
    paymentLoading,
    paymentResponse,
    paymentError,
    updatePayment,
    updatePaymentLoading,
    deletePayment,
    deletePaymentLoading,
  } = usePrismPaymentService();
  const canManagePrism = useMemo(() => {
    return canManageAnyPrismOffice(details);
  }, [details]);

  const tourSeenStorageKey = details?.email
    ? `prism-reports-tour-seen:${details.email}`
    : null;

  useEffect(() => {
    if (!canManagePrism || !tourSeenStorageKey) return;

    try {
      if (!window.localStorage.getItem(tourSeenStorageKey)) {
        setIsTourOpen(true);
      }
    } catch {
      // localStorage can be unavailable (private browsing, etc.) — the tour
      // simply won't auto-open in that case, no need to fail loudly.
    }
  }, [canManagePrism, tourSeenStorageKey]);

  const handleTourClose = () => {
    setIsTourOpen(false);

    if (!tourSeenStorageKey) return;

    try {
      window.localStorage.setItem(tourSeenStorageKey, "1");
    } catch {
      // Ignore — worst case the tour auto-opens again next visit.
    }
  };

  const tourSteps = useMemo(
    () => [
      {
        title: "Three kinds of reports",
        description:
          "Switch between Subscription Reports, Payment History, and Monthly Expenditures using these tabs.",
        target: () => reportTabsRef.current,
      },
      {
        title: "Filter and export",
        description:
          "Filter by renewal date, status, or urgency, then export the results to CSV or PDF.",
        target: () => subscriptionFiltersRef.current,
      },
      {
        title: "Subscription details",
        description: "Click any row to view that subscription's full details.",
        target: () => subscriptionTableRef.current,
      },
      {
        title: "Payment History & Monthly Expenditures",
        description:
          "Payment History lists and exports individual payments; Monthly Expenditures shows a month-by-month spending chart and breakdown.",
      },
    ],
    [],
  );

  useEffect(() => {
    getSubscriptions().catch(() => {
      // useAxios stores the load error for the inline alert.
    });
    if (canManagePrism) {
      getSubscriptionExportData().catch(() => {
        // useAxios stores the load error for the inline alert.
      });
      getPayments().catch(() => {
        // useAxios stores the load error for the inline alert.
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManagePrism]);

  const subscriptions = useMemo(
    () =>
      Array.isArray(subscriptionResponse?.data)
        ? subscriptionResponse.data.filter((item) => item.is_enabled !== false)
        : [],
    [subscriptionResponse],
  );

  const payments = useMemo(
    () => (Array.isArray(paymentResponse?.data) ? paymentResponse.data : []),
    [paymentResponse],
  );

  const exportSubscriptions = useMemo(
    () =>
      Array.isArray(subscriptionExportResponse?.data)
        ? subscriptionExportResponse.data.filter(
            (item) => item.is_enabled !== false,
          )
        : [],
    [subscriptionExportResponse],
  );


  const subscriptionExportReady = Array.isArray(
    subscriptionExportResponse?.data,
  );

  const subscriptionOptions = useMemo(
    () =>
      subscriptions
        .filter((subscription) =>
          canManagePrismRecord(details, subscription),
        )
        .map((subscription) => ({
          value: subscription.id,
          label: subscription.subscription_name || `Subscription #${subscription.id}`,
        })),
    [subscriptions, details],
  );

  const tableData = useMemo(() => {
    return buildSubscriptionReportRows({
      subscriptions,
      renewalDateRange,
      statusFilter,
      criticalityFilter,
      details,
      canManagePrism,
    });
  }, [
    subscriptions,
    renewalDateRange,
    statusFilter,
    criticalityFilter,
    details,
    canManagePrism,
  ]);

  const subscriptionExportTableData = useMemo(
    () =>
      buildSubscriptionReportRows({
        subscriptions: exportSubscriptions,
        renewalDateRange,
        statusFilter,
        criticalityFilter,
        details,
        canManagePrism,
      }),
    [
      exportSubscriptions,
      renewalDateRange,
      statusFilter,
      criticalityFilter,
      details,
      canManagePrism,
    ],
  );

  const paymentSubscriptionOptions = useMemo(() => {
    const optionsByValue = new Map();

    payments.forEach((payment) => {
      const value = getPaymentSubscriptionFilterValue(payment);

      if (optionsByValue.has(value)) {
        return;
      }

      optionsByValue.set(value, {
        value,
        label: getPaymentSubscriptionName(payment),
      });
    });

    return Array.from(optionsByValue.values()).sort((a, b) =>
      String(a.label).localeCompare(String(b.label)),
    );
  }, [payments]);

  const paymentTableData = useMemo(() => {
    return payments
      .map((payment) => ({
        key: payment.id,
        ...payment,
        subscription_name: getPaymentSubscriptionName(payment),
        subscription_filter_value: getPaymentSubscriptionFilterValue(payment),
        remarks_display: getPaymentRemarksDisplay(payment.remarks),
        remarks_accent: normalizeRemarksAccent(payment.remarks_accent),
      }))
      .filter((payment) => {
        const subscriptionMatches = subscriptionFilter
          ? payment.subscription_filter_value === String(subscriptionFilter)
          : true;

        return (
          subscriptionMatches && isDateInRange(payment.date_paid, paidDateRange)
        );
      })
      .sort((a, b) => comparePaymentsByPaidDate(b, a));
  }, [payments, paidDateRange, subscriptionFilter]);

  const selectedMonthlyYear = monthlyYear ? monthlyYear.year() : dayjs().year();

  const monthlyExpenditureData = useMemo(
    () =>
      buildMonthlyExpenditureData({
        payments,
        selectedYear: selectedMonthlyYear,
        subscriptionFilter: monthlySubscriptionFilter,
        getSubscriptionFilterValue: getPaymentSubscriptionFilterValue,
        getSubscriptionName: getPaymentSubscriptionName,
      }),
    [payments, selectedMonthlyYear, monthlySubscriptionFilter],
  );

  const totalMonthlySpend = useMemo(
    () => getTotalMonthlySpend(monthlyExpenditureData),
    [monthlyExpenditureData],
  );

  const renderExportFullOnly = (
    row,
    value,
    formatter = (fieldValue) => fieldValue || "-",
  ) => {
    if (!isFullPrismRecord(row, details)) {
      return "";
    }

    return formatter(value);
  };

  const columns = [
    {
      title: "Criticality",
      dataIndex: "criticality",
      key: "criticality",
      width: "7%",
      render: (value, record) =>
        renderRestrictedValue(record, value, details, renderCriticality),
    },
    {
      title: "Subscription Name",
      dataIndex: "subscription_name",
      key: "subscription_name",
      searchable: true,
      sortable: true,
      width: "9%",
      ellipsis: { showTitle: false },
      preserveRenderOnSearch: true,
      render: (value) => <PrismTruncatedText value={value} />,
    },
    {
      title: "Description",
      dataIndex: "subscription_details",
      key: "subscription_details",
      searchable: true,
      width: "9%",
      ellipsis: { showTitle: false },
      preserveRenderOnSearch: true,
      render: (value) => <PrismTruncatedText value={value} />,
    },
    {
      title: "Subscription Type",
      dataIndex: "subscription_type",
      key: "subscription_type",
      width: "6%",
      render: (value, record) =>
        renderRestrictedValue(record, value, details, (text) => (
          <PrismTruncatedText value={text} formatter={formatLabel} />
        )),
    },
    {
      title: "Subscriber Emails",
      dataIndex: "subscriber_email",
      key: "subscriber_email",
      searchable: true,
      width: "8%",
      ellipsis: { showTitle: false },
      preserveRenderOnSearch: true,
      render: (value, record) =>
        renderRestrictedValue(record, value, details, () => (
          <PrismTruncatedText value={formatSubscriberEmails(record)} />
        )),
    },
    {
      title: "Office",
      dataIndex: "subscriber_department",
      key: "subscriber_department",
      searchable: true,
      sortable: true,
      width: "7.5%",
      ellipsis: { showTitle: false },
      preserveRenderOnSearch: true,
      render: (value) => (
        <PrismTruncatedText value={value} formatter={formatOfficeName} />
      ),
    },
    {
      title: "Unit",
      dataIndex: "subscriber_sub_department",
      key: "subscriber_sub_department",
      searchable: true,
      width: "7.5%",
      ellipsis: { showTitle: false },
      preserveRenderOnSearch: true,
      render: (value) => (
        <PrismTruncatedText value={value} formatter={formatSubOfficeName} />
      ),
    },
    {
      title: "Status",
      dataIndex: "contract_status",
      key: "contract_status",
      width: "6%",
      render: (value, record) =>
        renderRestrictedValue(record, value, details, renderStatus),
    },
    {
      title: "Contract Start Date",
      dataIndex: "contract_start_date",
      key: "contract_start_date",
      width: "6%",
      sorter: (a, b) =>
        getDateTime(a.contract_start_date) - getDateTime(b.contract_start_date),
      render: (value, record) =>
        renderRestrictedValue(record, value, details, (text) => (
          <PrismTruncatedText value={text} formatter={formatDate} />
        )),
    },
    {
      title: "Contract End Date",
      dataIndex: "contract_end_date",
      key: "contract_end_date",
      width: "6%",
      sorter: (a, b) =>
        getDateTime(a.contract_end_date) - getDateTime(b.contract_end_date),
      render: (value, record) =>
        renderRestrictedValue(record, value, details, (text) => (
          <PrismTruncatedText value={text} formatter={formatDate} />
        )),
    },
    {
      title: "Renewal Date",
      dataIndex: "contract_renewal_date",
      key: "contract_renewal_date",
      width: "6%",
      sorter: (a, b) =>
        getDateTime(a.contract_renewal_date) -
        getDateTime(b.contract_renewal_date),
      render: (value, record) =>
        renderRestrictedValue(record, value, details, (text) => (
          <PrismTruncatedText value={text} formatter={formatDate} />
        )),
    },
    {
      title: "Renewal Frequency",
      dataIndex: "contract_renewal_frequency",
      key: "contract_renewal_frequency",
      width: "6%",
      render: (value, record) =>
        renderRestrictedValue(record, value, details, (text) => (
          <PrismTruncatedText value={text} formatter={formatLabel} />
        )),
    },
    {
      title: "Auto-renewal",
      dataIndex: "auto_renewal_status",
      key: "auto_renewal_status",
      width: "6%",
      render: (value, record) =>
        renderRestrictedValue(record, value, details, renderAutoRenewal),
    },
    {
      title: "Billing Cycle",
      dataIndex: "billing_cycle",
      key: "billing_cycle",
      width: "5%",
      render: (value, record) =>
        renderRestrictedValue(record, value, details, (text) => (
          <PrismTruncatedText value={text} formatter={formatLabel} />
        )),
    },
    {
      title: "Payee Name",
      dataIndex: "account_holder_name",
      key: "account_holder_name",
      searchable: true,
      width: "5%",
      ellipsis: { showTitle: false },
      preserveRenderOnSearch: true,
      render: (value, record) =>
        renderRestrictedValue(record, value, details, (text) => (
          <PrismTruncatedText value={text} />
        )),
    },
  ];

  const subscriptionColumns = canManagePrism
    ? columns
    : columns
        .filter((column) =>
          LIMITED_PRISM_SUBSCRIPTION_FIELD_KEYS.has(column.key),
        )
        .map((column) => ({
          ...column,
          width: REGULAR_USER_SUBSCRIPTION_COLUMN_WIDTHS[column.key],
        }));

  const basePaymentColumns = [
    {
      title: "Subscription Name",
      dataIndex: "subscription_name",
      key: "subscription_name",
      searchable: true,
      sortable: true,
      preserveRenderOnSearch: true,
      width: "22%",
      ellipsis: { showTitle: false },
      render: (value) => <PrismTruncatedText value={value} />,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: "13%",
      sorter: (a, b) => Number(a.amount || 0) - Number(b.amount || 0),
      render: renderBudget,
    },
    {
      title: "Paid Date",
      dataIndex: "date_paid",
      key: "date_paid",
      width: "13%",
      sorter: comparePaymentsByPaidDate,
      defaultSortOrder: "descend",
      render: formatDate,
    },
    {
      title: "Payment Method",
      dataIndex: "payment_method",
      key: "payment_method",
      width: "14%",
      render: formatLabel,
    },
    {
      title: "Remarks",
      dataIndex: "remarks_display",
      key: "remarks",
      searchable: true,
      preserveRenderOnSearch: true,
      width: "23%",
      render: (value) => <PrismTruncatedText value={value} lines={2} />,
      onCell: (record) => ({
        className:
          record.remarks_display === "-"
            ? "prism-payment-remarks-cell--empty"
            : `prism-payment-remarks-cell prism-payment-remarks-cell--${record.remarks_accent}`,
      }),
    },
  ];

  const paymentColumns = canManagePrism
    ? [
        ...basePaymentColumns,
        {
          title: "Actions",
          key: "actions",
          width: "15%",
          render: (_, record) => {
            const scope = getPaymentScope(record);
            if (!canManagePrismScope(details, scope.office, scope.unit)) {
              return "-";
            }

            return (
              <Space className="prism-report-payments-table__actions" size={4} wrap>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleEditPaymentOpen(record)}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deletePaymentLoading}
                  onClick={() => handleDeletePayment(record)}
                >
                  Delete
                </Button>
              </Space>
            );
          },
        },
      ]
    : basePaymentColumns;

  const monthlyExpenditureColumns = [
    {
      title: MONTHLY_EXPENDITURE_COLUMN_LABELS.month,
      dataIndex: "month",
      key: "month",
      width: "15%",
      render: renderText,
    },
    {
      title: MONTHLY_EXPENDITURE_COLUMN_LABELS.subscriptionBreakdown,
      dataIndex: "subscriptionBreakdown",
      key: "subscriptionBreakdown",
      width: "60%",
      render: (breakdown) =>
        breakdown?.length ? (
          <div className="prism-report-monthly-table__breakdown">
            {breakdown.map((item) => {
              const displayValue = `${item.subscriptionName}: ${renderBudget(
                item.total,
              )}`;

              return (
                <PrismTruncatedText key={item.key} value={displayValue} />
              );
            })}
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: MONTHLY_EXPENDITURE_COLUMN_LABELS.totalSpend,
      dataIndex: "total",
      key: "total",
      width: "25%",
      render: renderBudget,
    },
  ];

  const subscriptionExportColumns = [
    {
      header: "Criticality",
      pdfWidth: 5,
      pdfBlankWhenEmpty: true,
      accessor: (row) =>
        renderExportFullOnly(
          row,
          row.criticality,
          (value) => CRITICALITY_LABELS[value] || "-",
        ),
    },
    {
      header: "Subscription Name",
      pdfHeader: "Subscription",
      pdfWidth: 10.5,
      pdfWrapLength: 20,
      accessor: (row) => row.subscription_name,
    },
    {
      header: "Description",
      pdfWidth: 13,
      pdfWrapLength: 24,
      accessor: (row) => row.subscription_details,
    },
    {
      header: "Subscription Type",
      pdfHeader: "Type",
      pdfWidth: 5,
      pdfBlankWhenEmpty: true,
      accessor: (row) =>
        renderExportFullOnly(row, row.subscription_type, formatLabel),
    },
    {
      header: "Subscriber Emails",
      pdfHeader: "Email",
      pdfWidth: 7,
      pdfWrapLength: 22,
      pdfBlankWhenEmpty: true,
      accessor: (row) =>
        renderExportFullOnly(row, formatSubscriberEmails(row)),
    },
    {
      header: "Office",
      pdfHeader: "Office",
      pdfWidth: 9.5,
      pdfWrapLength: 22,
      accessor: (row) => formatOfficeName(row.subscriber_department),
    },
    {
      header: "Unit",
      pdfHeader: "Unit",
      pdfWidth: 9,
      pdfWrapLength: 22,
      accessor: (row) => formatSubOfficeName(row.subscriber_sub_department),
    },
    {
      header: "Status",
      pdfWidth: 4.2,
      pdfBlankWhenEmpty: true,
      accessor: (row) =>
        renderExportFullOnly(row, row.contract_status, formatLabel),
    },
    {
      header: "Contract Start Date",
      pdfHeader: "Start",
      pdfWidth: 4.8,
      pdfBlankWhenEmpty: true,
      accessor: (row) =>
        renderExportFullOnly(row, row.contract_start_date, formatDate),
    },
    {
      header: "Contract End Date",
      pdfHeader: "End",
      pdfWidth: 4.8,
      pdfBlankWhenEmpty: true,
      accessor: (row) =>
        renderExportFullOnly(row, row.contract_end_date, formatDate),
    },
    {
      header: "Renewal Date",
      pdfHeader: "Renewal",
      pdfWidth: 4.8,
      pdfBlankWhenEmpty: true,
      accessor: (row) =>
        renderExportFullOnly(row, row.contract_renewal_date, formatDate),
    },
    {
      header: "Renewal Frequency",
      pdfHeader: "Frequency",
      pdfWidth: 5,
      pdfBlankWhenEmpty: true,
      accessor: (row) =>
        renderExportFullOnly(
          row,
          row.contract_renewal_frequency,
          formatLabel,
        ),
    },
    {
      header: "Auto-renewal",
      pdfHeader: "Auto-renew",
      pdfWidth: 4.3,
      pdfBlankWhenEmpty: true,
      accessor: (row) =>
        renderExportFullOnly(
          row,
          row.auto_renewal_status,
          (value) => (value ? "Enabled" : "Disabled"),
        ),
    },
    {
      header: "Billing Cycle",
      pdfHeader: "Billing",
      pdfWidth: 4.6,
      pdfBlankWhenEmpty: true,
      accessor: (row) =>
        renderExportFullOnly(row, row.billing_cycle, formatLabel),
    },
    {
      header: "Payee Name",
      pdfHeader: "Payee",
      pdfWidth: 8.5,
      pdfWrapLength: 20,
      pdfBlankWhenEmpty: true,
      accessor: (row) => renderExportFullOnly(row, row.account_holder_name),
    },
    {
      header: "Bank Name",
      pdfHeader: "Bank",
      pdfWidth: 6,
      pdfBlankWhenEmpty: true,
      accessor: (row) => renderExportFullOnly(row, row.bank_name),
    },
  ];

  const paymentExportColumns = [
    {
      header: "Subscription Name",
      pdfHeader: "Subscription",
      pdfWidth: 27,
      pdfWrapLength: 28,
      accessor: (row) => row.subscription_name,
    },
    {
      header: "Amount",
      pdfWidth: 15,
      pdfAlign: "right",
      accessor: (row) => renderBudget(row.amount),
    },
    {
      header: "Paid Date",
      pdfWidth: 13,
      accessor: (row) => formatDate(row.date_paid),
    },
    {
      header: "Payment Method",
      pdfHeader: "Method",
      pdfWidth: 15,
      accessor: (row) => formatLabel(row.payment_method),
    },
    {
      header: "Remarks",
      pdfWidth: 30,
      pdfWrapLength: 34,
      accessor: (row) => row.remarks,
    },
  ];

  const monthlyExportRows = monthlyExpenditureData.map((row) => ({
    ...row,
    formattedSubscriptionBreakdown: formatSubscriptionBreakdown(
      row.subscriptionBreakdown,
    ),
    formattedTotal: renderBudget(row.total),
  }));

  const monthlyExportColumns = [
    {
      header: MONTHLY_EXPENDITURE_COLUMN_LABELS.month,
      pdfWidth: 18,
      accessor: (row) => row.month,
    },
    {
      header: MONTHLY_EXPENDITURE_COLUMN_LABELS.subscriptionBreakdown,
      pdfWidth: 52,
      pdfWrapLength: 32,
      pdfPreserveLineBreaks: true,
      accessor: (row) => row.formattedSubscriptionBreakdown,
    },
    {
      header: MONTHLY_EXPENDITURE_COLUMN_LABELS.totalSpend,
      pdfWidth: 30,
      pdfAlign: "right",
      accessor: (row) => row.formattedTotal,
    },
  ];

  const subscriptionExportFilters = [
    { label: "Renewal Date", value: getDateRangeLabel(renewalDateRange) },
    { label: "Status", value: statusFilter ? formatLabel(statusFilter) : "All" },
    {
      label: "Criticality",
      value: criticalityFilter ? CRITICALITY_LABELS[criticalityFilter] : "All",
    },
  ];

  const paymentExportFilters = [
    { label: "Paid Date", value: getDateRangeLabel(paidDateRange) },
    {
      label: "Subscription",
      value:
        paymentSubscriptionOptions.find(
          (option) => option.value === subscriptionFilter,
        )?.label || "All",
    },
  ];

  const monthlyExportFilters = [
    { label: "Year", value: String(selectedMonthlyYear) },
    {
      label: "Subscription",
      value:
        paymentSubscriptionOptions.find(
          (option) => option.value === monthlySubscriptionFilter,
        )?.label || "All",
    },
  ];

  const handleSubscriptionCsvExport = () => {
    if (!subscriptionExportReady) {
      message.error("Secure subscription export data is not available.");
      return;
    }

    downloadCsv(
      `prism_subscriptions_report_${getReportFileDate()}.csv`,
      subscriptionExportColumns,
      subscriptionExportTableData,
    );
  };

  const handlePaymentCsvExport = () => {
    downloadCsv(
      `prism_payment_history_${getReportFileDate()}.csv`,
      paymentExportColumns,
      paymentTableData,
    );
  };

  const errorMessage = getErrorMessage(
    subscriptionError,
    "Failed to load PRISM subscription reports.",
  );
  const paymentErrorMessage = getErrorMessage(
    paymentError,
    "Failed to load PRISM payment history.",
  );
  const subscriptionExportErrorMessage = getErrorMessage(
    subscriptionExportError,
    "Failed to prepare secure PRISM subscription exports.",
  );

  const toFormDateTime = (value) => {
    if (!value) return null;

    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  };

  const getPaymentFormValues = (payment = {}) => ({
    subscription_id: payment.subscription_id,
    amount:
      payment.amount === null || payment.amount === undefined
        ? null
        : Number(payment.amount),
    date_paid: toFormDateTime(payment.date_paid),
    payment_method: payment.payment_method || "other",
    remarks: payment.remarks || null,
    remarks_accent:
      payment.remarks_accent || DEFAULT_REMARKS_ACCENT,
  });

  const handleEditPaymentOpen = (record) => {
    const scope = getPaymentScope(record);
    if (!canManagePrismScope(details, scope.office, scope.unit)) {
      message.error("You are not allowed to edit payments for this office/unit.");
      return;
    }

    setSelectedPayment(record);
    setEditPaymentSubmitError(null);
    editPaymentForm.resetFields();
    editPaymentForm.setFieldsValue(getPaymentFormValues(record));
    setIsEditPaymentModalOpen(true);
  };

  const handleEditPayment = async (values) => {
    if (!selectedPayment?.id) {
      message.error("No payment selected.");
      return;
    }

    try {
      setEditPaymentSubmitError(null);
      await updatePayment(selectedPayment.id, values);
      message.success("Payment updated successfully.");
      setIsEditPaymentModalOpen(false);
      setSelectedPayment(null);
      editPaymentForm.resetFields();
      getPayments();
    } catch (error) {
      const submitError = error?.response?.data || error?.message;
      setEditPaymentSubmitError(submitError);
      message.error(getErrorMessage(submitError, "Failed to update payment."));
    }
  };

  const handleDeletePayment = (record) => {
    const scope = getPaymentScope(record);
    if (!canManagePrismScope(details, scope.office, scope.unit)) {
      message.error("You are not allowed to delete payments for this office/unit.");
      return;
    }

    setDeletePaymentSubmitError(null);
    Modal.confirm({
      title: "Delete Payment",
      content: `Are you sure you want to delete this payment for "${record.subscription_name}"? This action cannot be undone.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deletePayment(record.id);
          message.success("Payment deleted successfully.");
          getPayments();
        } catch (error) {
          const submitError = error?.response?.data || error?.message;
          setDeletePaymentSubmitError(submitError);
          message.error(
            getErrorMessage(submitError, "Failed to delete payment."),
          );
        }
      },
    });
  };

  const handleEditPaymentCancel = () => {
    setIsEditPaymentModalOpen(false);
    setSelectedPayment(null);
    setEditPaymentSubmitError(null);
    editPaymentForm.resetFields();
  };

  const subscriptionReport = (
    <>
      <Space ref={subscriptionFiltersRef} style={{ marginBottom: 12 }} wrap>
        <RangePicker
          format="YYYY-MM-DD"
          value={renewalDateRange}
          onChange={(value) => setRenewalDateRange(value)}
        />
        {canManagePrism && (
          <>
            <Select
              allowClear
              style={{ width: 180 }}
              placeholder="All Statuses"
              value={statusFilter}
              options={STATUS_FILTER_OPTIONS}
              onChange={(value) => setStatusFilter(value || null)}
            />
            <Select
              allowClear
              style={{ width: 180 }}
              placeholder="All Criticality"
              value={criticalityFilter}
              options={CRITICALITY_FILTER_OPTIONS}
              onChange={(value) => setCriticalityFilter(value || null)}
            />
          </>
        )}
        {canManagePrism && (
          <>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleSubscriptionCsvExport}
              loading={subscriptionExportLoading}
              disabled={!subscriptionExportReady}
            >
              Export CSV
            </Button>
            {subscriptionExportReady ? (
              <PDFDownloadLink
                document={
                  <PrismReportPdfDocument
                    title="PRISM Subscriptions Report"
                    filters={subscriptionExportFilters}
                    columns={subscriptionExportColumns}
                    rows={subscriptionExportTableData}
                    summary={`${subscriptionExportTableData.length} subscription record(s)`}
                  />
                }
                fileName={`prism_subscriptions_report_${getReportFileDate()}.pdf`}
                style={{ textDecoration: "none" }}
              >
                {({ loading }) => (
                  <Button icon={<DownloadOutlined />} loading={loading}>
                    Export PDF
                  </Button>
                )}
              </PDFDownloadLink>
            ) : (
              <Button
                icon={<DownloadOutlined />}
                loading={subscriptionExportLoading}
                disabled
              >
                Export PDF
              </Button>
            )}
          </>
        )}
      </Space>

      {subscriptionExportError && canManagePrism && (
        <Alert
          type="error"
          showIcon
          message="Unable to prepare subscription exports"
          description={
            typeof subscriptionExportErrorMessage === "string"
              ? subscriptionExportErrorMessage
              : "Please try again later."
          }
          style={{ marginBottom: 12 }}
        />
      )}

      {subscriptionError && (
        <Alert
          type="error"
          showIcon
          message="Unable to load subscription report"
          description={
            typeof errorMessage === "string"
              ? errorMessage
              : "Please try again later."
          }
          style={{ marginBottom: 12 }}
        />
      )}

      <div ref={subscriptionTableRef}>
      <PvTable
        className={`prism-report-subscriptions-table${
          canManagePrism ? " prism-report-subscriptions-table--full" : ""
        }`}
        data={tableData}
        columns={subscriptionColumns}
        loading={subscriptionLoading}
        reverse={false}
        tableLayout="fixed"
        getRowAriaLabel={(record) =>
          `View details for ${
            record.subscription_name || "unnamed subscription"
          }`
        }
        interactiveRows
        onRowClick={setSelectedSubscription}
      />
      </div>

      <PrismSubscriptionDetailsModal
        details={details}
        onClose={() => setSelectedSubscription(null)}
        open={Boolean(selectedSubscription)}
        subscription={selectedSubscription}
      />
    </>
  );

  const paymentHistoryReport = (
    <>
      <Space style={{ marginBottom: 12 }} wrap>
        <RangePicker
          format="YYYY-MM-DD"
          value={paidDateRange}
          onChange={(value) => setPaidDateRange(value)}
        />
        <Select
          allowClear
          showSearch
          style={{ width: 280 }}
          placeholder="All Subscriptions"
          value={subscriptionFilter}
          options={paymentSubscriptionOptions}
          optionRender={renderSubscriptionOption}
          labelRender={renderSubscriptionLabel}
          onChange={(value) => setSubscriptionFilter(value || null)}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
        />
        <Button icon={<DownloadOutlined />} onClick={handlePaymentCsvExport}>
          Export CSV
        </Button>
        <PDFDownloadLink
          document={
            <PrismReportPdfDocument
              title="PRISM Payment History Report"
              filters={paymentExportFilters}
              columns={paymentExportColumns}
              rows={paymentTableData}
              summary={`${paymentTableData.length} payment record(s)`}
            />
          }
          fileName={`prism_payment_history_${getReportFileDate()}.pdf`}
          style={{ textDecoration: "none" }}
        >
          {({ loading }) => (
            <Button icon={<DownloadOutlined />} loading={loading}>
              Export PDF
            </Button>
          )}
        </PDFDownloadLink>
      </Space>

      {paymentError && (
        <Alert
          type="error"
          showIcon
          message="Unable to load payment history"
          description={
            typeof paymentErrorMessage === "string"
              ? paymentErrorMessage
              : "Please try again later."
          }
          style={{ marginBottom: 12 }}
        />
      )}

      {deletePaymentSubmitError && (
        <Alert
          type="error"
          showIcon
          message="Unable to delete payment"
          description={getErrorMessage(
            deletePaymentSubmitError,
            "Failed to delete payment.",
          )}
          closable
          onClose={() => setDeletePaymentSubmitError(null)}
          style={{ marginBottom: 12 }}
        />
      )}

      <PvTable
        className="prism-report-payments-table"
        data={paymentTableData}
        columns={paymentColumns}
        loading={paymentLoading}
        reverse={false}
        tableLayout="fixed"
        onRowClick={() => {}}
      />

      <Popup
        title="Edit Payment"
        isOpen={isEditPaymentModalOpen}
        footer={null}
        className="prism-payment-popup"
        style={{ top: 20 }}
        width={820}
        onCancel={handleEditPaymentCancel}
      >
        <div className="prism-payment-modal-body">
          {editPaymentSubmitError && (
            <Alert
              type="error"
              showIcon
              message="Unable to update payment"
              description={getErrorMessage(
                editPaymentSubmitError,
                "Failed to update payment.",
              )}
              style={{ marginBottom: 12 }}
            />
          )}
          <PrismPaymentForm
            form={editPaymentForm}
            loading={updatePaymentLoading}
            subscriptionOptions={subscriptionOptions}
            onCancel={handleEditPaymentCancel}
            onSubmit={handleEditPayment}
            mode="edit"
          />
        </div>
      </Popup>
    </>
  );

  const monthlyExpendituresReport = (
    <>
      <Space style={{ marginBottom: 12 }} wrap>
        <DatePicker
          allowClear={false}
          picker="year"
          value={monthlyYear}
          onChange={(value) => setMonthlyYear(value || dayjs())}
        />
        <Select
          allowClear
          showSearch
          style={{ width: 280 }}
          placeholder="All Subscriptions"
          value={monthlySubscriptionFilter}
          options={paymentSubscriptionOptions}
          optionRender={renderSubscriptionOption}
          labelRender={renderSubscriptionLabel}
          onChange={(value) => setMonthlySubscriptionFilter(value || null)}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
        />
        <PDFDownloadLink
          document={
            <PrismReportPdfDocument
              title="PRISM Monthly Expenditures"
              filters={monthlyExportFilters}
              columns={monthlyExportColumns}
              rows={monthlyExportRows}
              monthlyChartData={monthlyExportRows}
              summary={`Annual total: ${renderBudget(totalMonthlySpend)}`}
            />
          }
          fileName={`prism_monthly_expenditures_${selectedMonthlyYear}_${getReportFileDate()}.pdf`}
          style={{ textDecoration: "none" }}
        >
          {({ loading }) => (
            <Button icon={<DownloadOutlined />} loading={loading}>
              Export PDF
            </Button>
          )}
        </PDFDownloadLink>
      </Space>

      {paymentError && (
        <Alert
          type="error"
          showIcon
          message="Unable to load monthly expenditures"
          description={
            typeof paymentErrorMessage === "string"
              ? paymentErrorMessage
              : "Please try again later."
          }
          style={{ marginBottom: 12 }}
        />
      )}

      <Spin spinning={paymentLoading}>
        {totalMonthlySpend > 0 ? (
          <MonthlySpendChart data={monthlyExpenditureData} />
        ) : (
          <div style={{ padding: 48 }}>
            <Empty description="No monthly expenditures found for the selected filters." />
          </div>
        )}
      </Spin>

      <PvTable
        className="prism-report-monthly-table"
        data={monthlyExpenditureData}
        columns={monthlyExpenditureColumns}
        loading={paymentLoading}
        maxActiveRow={12}
        reverse={false}
        tableLayout="fixed"
        onRowClick={() => {}}
      />
    </>
  );

  return (
    <div ref={reportTabsRef}>
      <Tabs
        className="prism-reports-tabs"
        activeKey={canManagePrism ? activeReportTab : "subscriptions"}
        onChange={(key) => dispatch(setPrismReportsTab(key))}
        tabBarStyle={{ marginBottom: 16 }}
        tabBarExtraContent={
          canManagePrism ? (
            <Tooltip title="Take a tour of this page">
              <Button
                icon={<QuestionCircleOutlined />}
                onClick={() => setIsTourOpen(true)}
                size="small"
              />
            </Tooltip>
          ) : null
        }
        items={[
          {
            key: "subscriptions",
            label: "Subscriptions",
            children: subscriptionReport,
          },
          ...(canManagePrism ? [{
            key: "payment-history",
            label: "Payment History",
            children: paymentHistoryReport,
          }] : []),
          ...(canManagePrism ? [{
            key: "monthly-expenditures",
            label: "Monthly Expenditures",
            children: monthlyExpendituresReport,
          }] : []),
        ]}
      />
      {canManagePrism && (
        <Tour open={isTourOpen} onClose={handleTourClose} steps={tourSteps} />
      )}
    </div>
  );
};

export default PrismReports;
